import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Star, ShoppingCart, Scale, Check, Eye, ImageOff } from "lucide-react";
import { useCart } from "../context/CartContext";
import { useProCart } from "../context/ProCartContext";
import { useCompare } from "../context/CompareContext";
import { productPath } from "../utils/links";

const eur = (n) => `${Number(n).toFixed(2).replace(".", ",")} €`;
const LOW_STOCK = 5;

export const ProductCardSkeleton = () => (
  <div className="bg-[#10131A] rounded-2xl border border-[#1E232D] overflow-hidden flex flex-col" data-testid="product-card-skeleton">
    <div className="h-48 shimmer" />
    <div className="p-4 space-y-3">
      <div className="h-3 w-16 rounded shimmer" />
      <div className="h-4 w-full rounded shimmer" />
      <div className="h-4 w-2/3 rounded shimmer" />
      <div className="h-7 w-24 rounded shimmer mt-2" />
      <div className="h-10 w-full rounded-lg shimmer" />
    </div>
  </div>
);

const availabilityOf = (product) => product.availability || (product.category === "performance" ? { available: true, status: "on_order" } : null);

export const Badges = ({ product, isNew }) => {
  const discount = product.old_price ? Math.round(((product.old_price - product.price) / product.old_price) * 100) : 0;
  const low = product.stock > 0 && product.stock <= LOW_STOCK;
  const av = availabilityOf(product);
  return (
    <div className="absolute top-3 left-3 z-10 flex flex-col gap-1.5 items-start">
      {discount > 0 && <span data-testid={`badge-promo-${product.id}`} className="badge-promo text-[11px] font-black italic px-2 py-0.5 rounded-md shadow-lg shadow-red-950/50">PROMO -{discount}%</span>}
      {isNew && <span data-testid={`badge-new-${product.id}`} className="badge-new text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md">Nouveau</span>}
      {av && <span data-testid={`badge-avail-${product.id}`} className={`text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md ${av.status === "in_stock" ? "bg-emerald-950/80 text-emerald-400" : av.status === "discontinued" ? "bg-slate-800 text-slate-300" : "bg-amber-950/80 text-amber-300"}`}>{av.status === "in_stock" ? "En stock" : av.status === "discontinued" ? "Produit arrêté" : "Sur commande"}</span>}
      {!av && low && <span data-testid={`badge-low-${product.id}`} className="badge-low text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md">Plus que {product.stock}</span>}
      {!av && product.stock <= 0 && <span data-testid={`badge-out-${product.id}`} className="bg-slate-800 text-slate-300 text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md">Rupture</span>}
    </div>
  );
};

const useTilt = () => {
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current; if (!el || window.matchMedia("(hover: none)").matches) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5, y = (e.clientY - r.top) / r.height - 0.5;
    el.style.transform = `perspective(900px) rotateX(${(-y * 6).toFixed(2)}deg) rotateY(${(x * 8).toFixed(2)}deg) translateY(-4px)`;
  };
  const onLeave = () => { if (ref.current) ref.current.style.transform = ""; };
  return { ref, onMouseMove: onMove, onMouseLeave: onLeave };
};

const ProductCard = ({ product, channel = "perso", isNew = false }) => {
  const perso = useCart();
  const pro = useProCart();
  const addItem = channel === "pro" ? pro.addItem : perso.addItem;
  const compare = useCompare();
  const compared = compare?.has(product.id);
  const tilt = useTilt();
  const [added, setAdded] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const av = availabilityOf(product);
  const out = av ? !av.available : product.stock <= 0;

  const add = () => { if (out) return; addItem(product); setAdded(true); setTimeout(() => setAdded(false), 1400); };

  return (
    <div {...tilt} data-testid={`product-card-${product.id}`} className="tilt group relative bg-[#10131A] rounded-2xl border border-[#1E232D] hover:border-[#E01F1F]/50 overflow-hidden flex flex-col">
      <Link to={productPath(product)} className="relative block h-48 bg-[#08090C] overflow-hidden">
        <Badges product={product} isNew={isNew} />
        {!loaded && !failed && <div className="absolute inset-0 shimmer" />}
        {failed && <div className="absolute inset-0 flex items-center justify-center text-slate-600"><ImageOff size={32} /></div>}
        <img src={product.image} alt={product.name} loading="lazy" decoding="async" onLoad={() => setLoaded(true)} onError={() => setFailed(true)} className={`w-full h-full object-cover group-hover:scale-105 transition-all duration-500 ${loaded ? "opacity-100" : "opacity-0"}`} />
        <div className="absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-[#10131A] to-transparent pointer-events-none" />
        <span className="absolute bottom-3 left-3 z-10 inline-flex items-center gap-1 text-[11px] font-semibold text-slate-200 bg-black/60 backdrop-blur px-2 py-1 rounded-md opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300"><Eye size={12} /> Voir la fiche</span>
      </Link>
      <div className="absolute top-3 right-3 z-10 flex flex-col gap-1.5">
        {compare && channel !== "pro" && (
          <button type="button" data-testid={`product-compare-${product.id}`} onClick={() => compare.toggle(product.id)} title={compared ? "Retirer du comparateur" : "Comparer"} aria-pressed={compared}
            className={`w-8 h-8 rounded-full flex items-center justify-center border transition-colors ${compared ? "bg-[#E01F1F] border-[#E01F1F] text-white" : "bg-black/60 border-white/15 text-slate-200 hover:bg-[#E01F1F] hover:border-[#E01F1F] hover:text-white"}`}>
            <Scale size={15} />
          </button>
        )}
        {!out && (
          <button type="button" data-testid={`product-quick-add-${product.id}`} onClick={add} title="Ajouter au panier"
            className={`w-8 h-8 rounded-full flex items-center justify-center border transition-all duration-300 md:opacity-0 md:translate-x-2 md:group-hover:opacity-100 md:group-hover:translate-x-0 ${added ? "bg-emerald-600 border-emerald-600 text-white" : "bg-[#E01F1F] border-[#E01F1F] text-white hover:bg-[#C81818]"}`}>
            {added ? <Check size={15} /> : <ShoppingCart size={15} />}
          </button>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <span className="text-[11px] font-bold text-[#FF3B3B] uppercase tracking-[0.14em]">{product.brand}</span>
        <Link to={productPath(product)} className="font-semibold text-slate-50 text-[15px] leading-snug mt-1 line-clamp-2 hover:text-[#FF3B3B] transition-colors min-h-[42px]">
          {product.name}
        </Link>
        <div className="flex items-center justify-between mt-2 text-[13px] text-slate-400">
          {product.reviews > 0 ? (
            <span className="flex items-center gap-1" data-testid={`product-rating-${product.id}`}><Star size={14} className="fill-[#f5a623] text-[#f5a623]" /><span className="font-semibold text-slate-200">{product.rating}</span><span>({product.reviews})</span></span>
          ) : (
            <span className="flex items-center gap-1 text-slate-500" data-testid={`product-rating-${product.id}`}><Star size={14} className="text-slate-600" /> Aucun avis</span>
          )}
          <span data-testid={`product-stock-${product.id}`} className={`text-[11px] font-bold ${out ? "text-red-400" : (av ? av.status !== "in_stock" : product.stock <= LOW_STOCK) ? "text-amber-300" : "text-emerald-400"}`}>{av ? (out ? "Produit arrêté" : av.status === "in_stock" ? "En stock" : "Sur commande") : out ? "Rupture" : product.stock <= LOW_STOCK ? "Stock faible" : "En stock"}</span>
        </div>
        <div className="mt-3 flex items-end gap-2 flex-wrap">
          {product.pro_price ? (
            <>
              <span data-testid={`product-pro-price-${product.id}`} className="text-2xl font-black text-emerald-400">{eur(product.pro_price)}</span>
              <span data-testid={`product-list-price-${product.id}`} className="text-sm text-slate-500 line-through mb-1">{eur(product.price)}</span>
              <span data-testid={`product-pro-badge-${product.id}`} className="mb-1 bg-emerald-950/80 text-emerald-400 border border-emerald-800/60 text-[10px] font-bold px-1.5 py-0.5 rounded uppercase">Prix pro -{product.pro_discount}%</span>
            </>
          ) : (
            <>
              <span data-testid={`product-price-${product.id}`} className="text-2xl font-black text-white">{eur(product.price)}</span>
              {product.old_price && <span data-testid={`product-old-price-${product.id}`} className="text-sm text-slate-500 line-through mb-1">{eur(product.old_price)}</span>}
            </>
          )}
        </div>
        <button onClick={add} disabled={out} data-testid={`product-add-${product.id}`} className={`mt-4 w-full rounded-lg py-2.5 font-bold flex items-center justify-center gap-2 active:scale-[0.98] transition-transform ${added ? "bg-emerald-600 text-white" : "btn-orange"} disabled:opacity-40`}>
          {added ? <><Check size={18} /> Ajouté</> : <><ShoppingCart size={18} /> {out ? "Indisponible" : "Ajouter"}</>}
        </button>
      </div>
    </div>
  );
};

export default ProductCard;
